import React from "react";
import styled from "styled-components";
import { DEFAULT_TAG_PALETTE, PLACEHOLDER_TAGS, UNASSIGNED_TAG } from "@lifeat/shared/task-management/TaskTagConstants";
import { canUserAssignTag } from "@lifeat/shared/task-management/TaskTagUtil";
import { isUserActiveProSubscriber } from "@lifeat/shared/user/UserUtil";
import { DropdownMenuComponents } from "@common/LifeAtDropdownMenuV2/DropdownMenuComponents";
import { useAppearanceContext } from "@contexts/AppearanceContext";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { useUserContext } from "@contexts/UserContext";
import { FontSize } from "@themes/Fonts";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { useTaskListContext } from "../TaskListContext";
import TagComponent from "./TagComponent";
import { DEFAULT_TAG_ID } from "./TaskItemConstants";
import { AddNewTagDropdownItem, DefaultTagIndicator, DropdownItem, EditTagButton } from "./TaskTagDropdownComponents";

/* ------------------------------------------------------------- */
/* -------------- Tag Assignment Dropdown Content -------------- */
/* ------------------------------------------------------------- */

const REASSIGN_TAG_LABEL = "Reassign tag";
const ASSIGN_TAG_LABEL = "Assign tag";

const RadioButtonItemContainer = styled.div`
    display: flex;
    gap: 7px;
`;
interface TagAssignmentDropdownContentProps {
    readonly selectedTagId: string;
    readonly handleTagUpdate: (tagId: string) => void;
}
export function TagAssignmentDropdownContent(props: TagAssignmentDropdownContentProps) {
    const { userData } = useUserContext();
    const { tags, defaultTagId } = useTaskListContext();
    const { setUpgradeCtaContext }: CallToActionContextType = useCallToActionContext();

    const isFreeUser = !isUserActiveProSubscriber(userData?.proUserInfo);
    const tagsToDisplay = isFreeUser && tags?.length === 0 ? PLACEHOLDER_TAGS : tags;

    const handleTagUpdate = (tagId: string) => {
        if (canUserAssignTag(userData)) {
            props.handleTagUpdate(tagId);
        } else {
            setUpgradeCtaContext("tags", UpgradeCTASource.FILTER_TAGS);
        }
    };
    return (
        <>
            <DropdownMenuComponents.HeaderItem
                label={DEFAULT_TAG_ID === props.selectedTagId ? ASSIGN_TAG_LABEL : REASSIGN_TAG_LABEL}
            />
            {tagsToDisplay.map(({ id: tagId, document: tag }) => (
                <DropdownItem key={tagId} onClick={() => handleTagUpdate(tagId)} isDisabled={isFreeUser}>
                    <RadioButtonItemContainer>
                        <input
                            type="radio"
                            readOnly
                            value={tagId}
                            checked={tagId === props.selectedTagId}
                            id={`tag-assignment-option-${tagId}`}
                        />
                        <label htmlFor={`tag-assignment-option-${tagId}`}>
                            <TagComponent tag={tag} disableHover={true} />
                        </label>
                    </RadioButtonItemContainer>
                    {tagId === defaultTagId && <DefaultTagIndicator />}
                    <EditTagButton tagId={tagId} tag={tag} />
                </DropdownItem>
            ))}
            <DropdownItem key={DEFAULT_TAG_ID} onClick={() => handleTagUpdate(DEFAULT_TAG_ID)}>
                <RadioButtonItemContainer>
                    <input
                        type="radio"
                        readOnly
                        value={DEFAULT_TAG_ID}
                        checked={DEFAULT_TAG_ID === props.selectedTagId}
                        id={`tag-assignment-option-default-tag`}
                    />
                    <label htmlFor={`tag-assignment-option-default-tag`}>
                        <TagComponent tag={UNASSIGNED_TAG} disableHover={true} />
                    </label>
                </RadioButtonItemContainer>
            </DropdownItem>
            <DropdownMenuComponents.Separator />
            <AddNewTagDropdownItem />
        </>
    );
}

/* ------------------------------------------------------------- */
/* ----------------- Add tag dropdown trigger ------------------ */
/* ------------------------------------------------------------- */

const ADD_TAG_LABEL = "+ add tag";

const AddTagButtonContainer = styled.div<{ backgroundColor: string; textColor: string }>`
    font-size: ${FontSize.small};
    padding: 2px 4px;
    border-radius: 2px;

    background-color: ${(props) => props.backgroundColor};
    color: ${(props) => props.textColor};
`;

interface AddTagDropdownTriggerProps {
    readonly className?: string;
}
export function AddTagDropdownTrigger(props: AddTagDropdownTriggerProps) {
    const { lightMode } = useAppearanceContext();
    const backgroundColor = lightMode ? DEFAULT_TAG_PALETTE.lighter : DEFAULT_TAG_PALETTE.darker;
    const textColor = lightMode ? DEFAULT_TAG_PALETTE.darker : DEFAULT_TAG_PALETTE.lighter;

    return (
        <AddTagButtonContainer backgroundColor={backgroundColor} textColor={textColor} className={props.className}>
            {ADD_TAG_LABEL}
        </AddTagButtonContainer>
    );
}
